// src/components/PhotoCard.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDownload } from "../hooks/useDownload";
import { useToast } from "./Toast";
import AdSlot from "./AdSlot";

const SIZES = [
  { key: "original", label: "Original", hint: "Full res" },
  { key: "large",    label: "Large",    hint: "~3200px"  },
  { key: "medium",   label: "Medium",   hint: "~1280px"  },
  { key: "small",    label: "Small",    hint: "~640px"   },
];

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const ExpandIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
  </svg>
);

const ShareIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/>
    <circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

// Share buttons config
const getShareLinks = (photo) => {
  const url   = encodeURIComponent("https://halro.online");
  const text  = encodeURIComponent(`Check out this free photo by ${photo.photographer} on Halro Images!`);
  const image = encodeURIComponent(photo.src.large);

  return [
    {
      name: "WhatsApp",
      color: "#25D366",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
      url: `https://wa.me/?text=${text}%20${url}`,
    },
    {
      name: "Twitter/X",
      color: "#000000",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      url: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
    },
    {
      name: "Facebook",
      color: "#1877F2",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      url: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    },
    {
      name: "Pinterest",
      color: "#E60023",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
        </svg>
      ),
      url: `https://pinterest.com/pin/create/button/?url=${url}&media=${image}&description=${text}`,
    },
  ];
};

const PhotoCard = ({ photo, onOpenModal }) => {
  const { user }                       = useAuth();
  const { downloadPhoto, downloading } = useDownload();
  const { success, error: toastError } = useToast();
  const navigate                       = useNavigate();

  const [hovered,    setHovered]    = useState(false);
  const [showSizes,  setShowSizes]  = useState(false);
  const [showShare,  setShowShare]  = useState(false);
  const [imgLoaded,  setImgLoaded]  = useState(false);

  const isDownloading = !!downloading[photo.id];

  const handleDownloadClick = (e) => {
    e.stopPropagation();
    if (!user) { navigate("/auth"); return; }
    setShowSizes(true);
    setShowShare(false);
  };

  const handleSizeSelect = async (e, size) => {
    e.stopPropagation();
    setShowSizes(false);
    try {
      await downloadPhoto(photo, size);
      success(`Downloading ${size} · ${photo.photographer}`);
    } catch {
      toastError("Download failed. Please try again.");
    }
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    setShowShare(v => !v);
    setShowSizes(false);
  };

  const handleShareOpen = (e, url) => {
    e.stopPropagation();
    window.open(url, "_blank", "width=600,height=500");
    setShowShare(false);
  };

  // Native share API (mobile)
  const handleNativeShare = async (e) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Photo by ${photo.photographer}`,
          text: `Check out this free photo on Halro Images!`,
          url: "https://halro.online",
        });
      } catch {}
    }
  };

  return (
    <div
      className="photo-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setShowSizes(false); setShowShare(false); }}
      style={{ aspectRatio: `${photo.width} / ${photo.height}` }}
    >
      {/* Source badge */}
      {imgLoaded && photo.source && (
        <div className={`photo-source-badge photo-source-badge--${photo.source}`}>
          {photo.source === "wallhaven" ? "HD" : "HD"}
        </div>
      )}

      {/* Skeleton */}
      {!imgLoaded && <div className="photo-card__skeleton" aria-hidden="true" />}

      {/* Image */}
      <img
        src={photo.src.large}
        alt={photo.alt || `Photo by ${photo.photographer}`}
        loading="lazy"
        decoding="async"
        className={`photo-card__img${imgLoaded ? " photo-card__img--loaded" : ""}`}
        onLoad={() => setImgLoaded(true)}
        onError={(e) => { e.target.closest(".photo-card").style.display = "none"; }}
      />

      {/* Hover overlay */}
      {hovered && (
        <div className="photo-card__overlay">
          {/* Top row: expand + share */}
          <div className="photo-card__top-row">
            <button className="photo-card__expand" onClick={(e) => { e.stopPropagation(); if (onOpenModal) onOpenModal(photo); }}
              aria-label="Preview" title="Preview">
              <ExpandIcon />
            </button>
            <button className={`photo-card__expand${showShare ? " photo-card__expand--active" : ""}`}
              onClick={handleShareClick} aria-label="Share" title="Share">
              <ShareIcon />
            </button>
          </div>

          {/* Share panel */}
          {showShare && (
            <div className="share-panel" onClick={(e) => e.stopPropagation()}>
              <p className="share-panel__label">Share this photo</p>
              <div className="share-panel__buttons">
                {getShareLinks(photo).map((s) => (
                  <button
                    key={s.name}
                    className="share-btn"
                    style={{ "--share-color": s.color }}
                    onClick={(e) => handleShareOpen(e, s.url)}
                    title={s.name}
                    aria-label={`Share on ${s.name}`}
                  >
                    {s.icon}
                    <span>{s.name}</span>
                  </button>
                ))}
                {/* Native share (mobile only) */}
                {navigator.share && (
                  <button className="share-btn share-btn--more"
                    onClick={handleNativeShare} title="More options">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/>
                      <circle cx="18" cy="19" r="3"/>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                    </svg>
                    <span>More</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Photographer */}
          {!showShare && (
            <a href={photo.photographer_url} target="_blank" rel="noopener noreferrer"
               className="photo-card__photographer" onClick={(e) => e.stopPropagation()}>
              <div className="photo-card__avatar">{photo.photographer[0].toUpperCase()}</div>
              <span className="photo-card__photographer-name">{photo.photographer}</span>
            </a>
          )}

          {/* Ad slot */}
          {!showShare && <AdSlot type="pre-download" />}

          {/* Download / size picker */}
          {!showShare && (
            showSizes ? (
              <div className="size-picker" onClick={(e) => e.stopPropagation()}>
                <p className="size-picker__label">Choose size</p>
                {SIZES.map(({ key, label, hint }) => (
                  <button key={key} className="size-picker__btn"
                    onClick={(e) => handleSizeSelect(e, key)} disabled={isDownloading}>
                    <span className="size-picker__name">{label}</span>
                    <span className="size-picker__hint">{hint}</span>
                  </button>
                ))}
                <button className="size-picker__cancel"
                  onClick={(e) => { e.stopPropagation(); setShowSizes(false); }}>
                  Cancel
                </button>
              </div>
            ) : (
              <button className="btn-download" onClick={handleDownloadClick}
                disabled={isDownloading}
                aria-label={`Download photo by ${photo.photographer}`}>
                {isDownloading ? <span className="spinner" /> : (
                  <><DownloadIcon />{user ? "Download" : "Sign in to download"}</>
                )}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoCard;
